import React, { createContext, useContext, useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, isFirebaseConfigured } from "../firebase/config";
import { authService, studentService } from "../firebase/services";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem("kt_theme") || "dark");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("kt_theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    if (!isFirebaseConfigured || !auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          const profile = await authService.getUserRole(user.uid, user.email);
          if (profile?.role === "student") {
            const studentDoc = await studentService.getStudentByUid(user.uid);
            const mergedProfile = studentDoc ? { ...profile, ...studentDoc, role: "student" } : profile;
            setUserProfile(mergedProfile);
          } else if (profile) {
            setUserProfile(profile);
          } else {
            // Never infer a role from email/name. Unknown authenticated users fail closed.
            setUserProfile(null);
          }
        } catch (error) {
          console.error("Auth context profile fetch error:", error);
          // Fail closed: no profile means no privileged role.
          setUserProfile(null);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    const { user, profile } = await authService.login(email, password);
    setCurrentUser(user);

    // The users/{uid} document intentionally stores only account/identity metadata.
    // Student academic/contact/photo fields live in students/{uid}. Always merge both
    // documents during an explicit login so the profile page is complete immediately
    // instead of waiting for a later auth-state refresh.
    let mergedProfile = profile;
    if (profile?.role === "student" && user?.uid) {
      try {
        const studentDoc = await studentService.getStudentByUid(user.uid);
        if (studentDoc) {
          mergedProfile = { ...profile, ...studentDoc, role: "student" };
        }
      } catch (error) {
        console.error("Could not load student profile after login:", error);
      }
    }

    setUserProfile(mergedProfile);
    return { user, profile: mergedProfile };
  };

  const logout = async () => {
    await authService.logout();
    setCurrentUser(null);
    setUserProfile(null);
  };

  const resetPassword = async (email) => {
    await authService.sendPasswordReset(email);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userProfile,
        loading,
        theme,
        toggleTheme,
        login,
        logout,
        resetPassword,
        isConfigured: isFirebaseConfigured,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
